import format from 'date-fns/format'
import formatDistanceStrict from 'date-fns/formatDistanceStrict'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import Row from '@/components/common/Row'
import Text from '@/components/common/Text'
import Button from '@/components/ui/Button'

import HeaderDropdown from './common/HeaderDropdown'
import BaseLink from '@/components/common/BaseLink'
import { formatDistanceToNow } from 'date-fns'
import { es, pt, ru, uk } from 'date-fns/locale'
import { useRouter } from 'next/router'
import Icon from '@/components/common/Icon'
import { userAmplitudeAnalytics } from '@/common/services/userAmplitudeAnalytics'
import { PAYMENT_SOURCE } from '@/common/constants/cookies'
import { useTranslations } from 'next-intl'

const HEADER_SUBCRIPTION = [
  {
    first: 'userSubscriptionDescriptionTop1',
    second: 'userSubscriptionDescriptionTop2',
  },
  {
    first: 'userSubscriptionDescriptionBottom1',
    second: 'userSubscriptionDescriptionBottom2',
  },
]

const locales = {
  uk,
  es,
  pt,
  ru,
}

const MAX_ALLOWED_SEARCHES = 300

const HeaderSubscription = ({ ...props }) => {
  const subscription = useSelector((store) => store.user.subscription)
  const t = useTranslations('common')
  const { locale } = useRouter()

  const parsedDate = useMemo(() => {
    return Date.parse(subscription.expiration_date)
      ? subscription.expiration_date
      : null
  }, [subscription])

  const expirationDate = useMemo(() => {
    return parsedDate
      ? format(new Date(subscription.expiration_date), 'yyyy-MM-dd')
      : ''
  }, [parsedDate])

  const isPayed = useMemo(() => {
    return subscription.type === 'payed' && parsedDate
  }, [parsedDate])

  const formatExpirationDate = (expirationDateStr) => {
    const expirationDate = new Date(expirationDateStr)
    const selectedLocale = locales[locale]

    return formatDistanceToNow(expirationDate, {
      addSuffix: true,
      locale: selectedLocale,
    })
  }

  const subscriptionType = subscription?.name?.toUpperCase()

  const content = useMemo(() => {
    return isPayed
      ? {
          trigger: {
            color: 'grey',
            text: `${t('packageInfo')}: ${subscription.name.toUpperCase()}
            `,
          },
          text: HEADER_SUBCRIPTION[0],
          tariffName: `Left: ${formatDistanceStrict(
            new Date(subscription.expiration_date),
            new Date(),
            { unit: 'day' }
          )}`,
          button: {
            link: `/premium`,
            text: `${t('headerSubscriptionExtendButton')}`,
          },
        }
      : {
          trigger: {
            color: 'red',
            text: `${t('headerSubscriptionDemo')}`,
          },
          text: HEADER_SUBCRIPTION[1],
          button: {
            link: `/premium`,
            text: `${t('headerSubscriptionPurchaseButton')}`,
          },
        }
  }, [subscription, locale])

  return (
    <HeaderDropdown
      triggerText={content.trigger.text}
      triggerColor={content.trigger.color}
      tariffName={content.tariffName}
      triggerClickCallback={() => {
        userAmplitudeAnalytics.trackEvent('sub_status_clicked', {
          type: subscription?.name,
        })
      }}
      isTriggerCallback={false}
      {...props}
    >
      {isPayed ? (
        <Row
          alignItems="center"
          justifyContent="space-between"
          className="mb-3"
        >
          <Text size="14">{t('endDate')}:</Text>
          <Text size="18" weight="500" color="grey">
            {formatExpirationDate(expirationDate)}
          </Text>
        </Row>
      ) : (
        <Row justifyContent="center" className="mb-3">
          <Text size="18" weight="500" color="red">
            {t('userSubscriptionTitle')}
          </Text>
        </Row>
      )}
      <Row alignItems="center" justifyContent="space-between" className="mb-3">
        <Text size="14">{t('packageInfo')}:</Text>
        <Text size="18" weight="500" color="grey">
          {subscriptionType}
        </Text>
      </Row>
      <Row alignItems="center" justifyContent="space-between" className="mb-3">
        <Text size="14">{t('searchesRemaining')}: </Text>
        <Text size="18" weight="500" color="grey">
          {subscription.remaining_searches} /{' '}
          {subscription.features.search.limit}
        </Text>
      </Row>
      {subscription.remaining_searches < MAX_ALLOWED_SEARCHES && (
        <Row
          alignItems="center"
          justifyContent="space-between"
          className="alert-warning"
        >
          <Text size="14" color="red" className="mr-2">
            {t.rich('remainingSearches', {
              remaining_searches: subscription.remaining_searches,
            })}
          </Text>
          <Icon icon={'info-circle'} size="24" color="red" />
        </Row>
      )}
      <BaseLink href="/premium" isChangePath={false}>
        <Button
          onClick={() => {
            userAmplitudeAnalytics.trackEvent(
              'sub_status_more_searches_clicked',
              {
                type: subscription?.name,
              }
            )
            sessionStorage.setItem(PAYMENT_SOURCE, 'header')
          }}
          size="small"
          color="primary"
          className="mt-4 mb-4 ml-auto"
        >
          {t('searchPromptMore')}
        </Button>
      </BaseLink>
      <Text size="14" className="mb-4">
        {t(`${content.text.first}`)}
        <br />
        <br />
        {t(`${content.text.second}`)}
      </Text>
      <BaseLink href={content.button.link} isChangePath={false}>
        <Button
          onClick={() => {
            userAmplitudeAnalytics.trackEvent(
              'sub_status_full_access_clicked',
              {
                type: subscription?.type,
              }
            )
            sessionStorage.setItem(PAYMENT_SOURCE, 'header')
          }}
          className="ml-auto"
        >
          {content.button.text}
        </Button>
      </BaseLink>
    </HeaderDropdown>
  )
}

export default HeaderSubscription
